import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ChatStore } from "@/stores/ChatStore";
import SettingsStore from "@/stores/SettingsStore";

export default function MessageInput() {
  const [message, setMessage] = useState("");
  const trimmed = message.trim();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const isHistoryOpen = ChatStore(state => state.isHistoryPanelOpen);
  const sendText = ChatStore(state => state.sendText);
  const addMessage = ChatStore(state => state.addMessage);
  const setIsForcedToInterrupt = ChatStore(state => state.setIsForcedToInterrupt);
  const inputMode = ChatStore(state => state.inputMode);
  const useSchadenKI = SettingsStore(state => state.useSchadenKI);

  useEffect(() => {
    if (isHistoryOpen) {
      // small timeout ensures panel open animation doesn't prevent focus
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [isHistoryOpen, inputMode]);

  const onSendMessage = () => {
    console.log("💬 [MessageInput] onSendMessage called", {
      trimmed,
      hasSendTextFunction: !!sendText,
      timestamp: new Date().toISOString()
    });

    if (!trimmed) {
      console.log("⚠️ [MessageInput] Message is empty, not sending");
      return;
    }

    if (!sendText) {
      console.error("❌ [MessageInput] sendText function is not available!");
      return;
    }

    console.log("📤 [MessageInput] Sending message via sendText function");
    // send via realtime
    sendText(trimmed);

    console.log("📝 [MessageInput] Adding optimistic message to history");
    // optimistic echo into history
    addMessage({ id: crypto.randomUUID(), sender: "user", transcript: trimmed, timestamp: new Date() });
    setMessage("");

    // Only interrupt ongoing responses when NOT using Schaden KI
    // (Schaden KI handles its own request lifecycle, interrupting would abort the request)
    if (!useSchadenKI) {
      console.log("🔄 [MessageInput] Setting forced interrupt flag (Realtime API)");
      setIsForcedToInterrupt(true);
    } else {
      console.log("⏭️ [MessageInput] Skipping interrupt flag (Schaden KI mode)");
    }

    // restore focus to the textarea so user can continue typing
    // small timeout ensures focus after state update / click handling
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSendMessage();
    }
    // Shift+Enter will naturally insert a newline, so do nothing else
  };

  return (
    <div className={'flex group'}>
      <Textarea
        ref={textareaRef}
        className={cn(
          "pl-4 pt-[0.8rem] min-h-[24px] max-h-[300px] [field-sizing:content]",
          "rounded-l-3xl rounded-r-none border-r-transparent resize-none text-sm",
          // show a 1px primary border when focused
          "focus:outline-none focus:border-l-primary focus:border-t-primary focus:border-b-primary"
        )}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={inputMode === "voice"}
      />
      <div className="p-2 w-10 rounded-r-3xl border-r border-t border-b flex items-end justify-center group-focus-within:border-primary">
        <div
          className={cn(
            "p-2 rounded-full cursor-pointer",
            trimmed ? "hover:bg-cta hover:text-white text-cta" : "opacity-40 cursor-not-allowed"
          )}
          onClick={onSendMessage}
          role="button"
          aria-disabled={!trimmed}
        >
          <Send size={14} />
        </div>
      </div>
    </div>
  );
}